import { LinearProgress } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useMemo } from "react";
import { useLocation, useParams } from "react-router";

import { rewriteUrl } from "@/utils/rewriteUrl";

import { ContentMemo } from "../Fullscreen/FullscreenCarousel";
import { useMediaItem } from "./hooks/useMediaItem";

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    flexGrow: 1,
    minHeight: "40px",
    height: "100%",
  },
  bodyContent: {
    width: "100%",
    height: "100%",
    backgroundColor: "#2c2c2c",
    overflow: "auto",
  },
  contentContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    width: "100%",
  },
  image: {
    width: "100%",
    objectFit: "contain",
    height: "100%",
    objectPosition: "50% 50%",
    maxWidth: "100%",
  },
  linkPreview: {
    width: "100%",
    height: "100%",
    border: 0,
  },
  document: {
    margin: "0 auto",
    justifyContent: "center",
    marginTop: "4rem",
    height: "100%",
    width: "100%",
    "& > div": {
      height: "100%",
      overflow: "hidden !important",
    },
  },
  loadingContainer: {
    display: "flex",
    "justify-content": "center",
    position: "absolute",
    transform: "translate(-50%, -50%)",
    top: "50%",
    left: "50%",
    justifyContent: "center",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
  },
  notEmbeddable: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    height: "100%",
  },
  audio: {
    position: "absolute",
    width: "30vw",
    minWidth: 300,
    top: "50%",
    left: "50%",
    transform: "translate(-50%)",
  },
  progressRoot: {},
}));

export default function MediaViewer(): JSX.Element {
  const classes = useStyles();
  const { propertyId, mediaId } = useParams();
  const { search } = useLocation();
  const { isLoading, setLoading, data } = useMediaItem(propertyId!, mediaId!);

  const rewrittenUrl = useMemo(() => {
    if (!data?.link) {
      return "";
    }

    try {
      const source = new URLSearchParams(search).get("source");
      if (source === "api") {
        return rewriteUrl(new URL(data.link as string));
      }
    } catch (error) {
      console.error("Invalid URL:", data.link, error);
    }

    return data.link;
  }, [data?.link, search]);

  return (
    <div className={classes.root} data-testid="media-viewer">
      <div className={classes.bodyContent}>
        {isLoading && (
          <div className={classes.loadingContainer} data-testid="loading-progress">
            <LinearProgress />
          </div>
        )}
        {data && (
          <div className={classes.contentContainer} data-testid="content-memo">
            <ContentMemo
              {...({
                classes,
                mediaItem: { ...data, link: rewrittenUrl },
                load: true,
                setLoading,
              } as {
                classes: typeof classes;
                mediaItem: typeof data & { link: string };
                load: boolean;
                setLoading: typeof setLoading;
              })}
            />
          </div>
        )}
      </div>
    </div>
  );
}
